import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

// Suppress the "Invalid Refresh Token: Refresh Token Not Found" console error.
// This fires when Supabase tries to auto-refresh a stale/expired token that was
// left in storage from a previous session. It is harmless — the session is cleared
// automatically — but it pollutes the console. We intercept it here and silently
// clear any stale auth tokens from storage instead.
if (typeof window !== 'undefined') {
  const _origError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    const msg = typeof args[0] === 'string' ? args[0] : (args[0] as any)?.message ?? '';
    const isInvalidToken =
      typeof msg === 'string' && (msg.includes('Invalid Refresh Token') || msg.includes('Refresh Token Not Found'));
    const isFailedFetch =
      typeof msg === 'string' && msg.includes('Failed to fetch') &&
      // Only suppress when it originates from Supabase auth (stack trace check)
      args.some((a) => {
        let s = typeof a === 'string' ? a : (a as any)?.stack ?? '';
        return typeof s === 'string' && (s.includes('supabase') || s.includes('GoTrueClient') || s.includes('auth-js'));
      });

    if (isInvalidToken || isFailedFetch) {
      // Clear stale auth tokens from localStorage
      try {
        Object.keys(localStorage)
          .filter((k) => k.includes('supabase') || k.includes('sb-') || k.startsWith(PFX))
          .forEach((k) => localStorage.removeItem(k));
      } catch { /* ignore */ }
      // Clear stale auth cookies
      try {
        document.cookie.split(';').forEach((c) => {
          const name = c.trim().split('=')[0];
          if (name.includes('supabase') || name.includes('sb-') || name.startsWith(PFX)) {
            document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
          }
        });
      } catch { /* ignore */ }
      // Reset the singleton so the next createClient() call starts fresh
      try { (globalThis as any)[GLOBAL_KEY] = undefined; } catch { /* ignore */ }
      return; // swallow the error log
    }
    _origError(...args);
  };
}

// Use a globalThis key so the singleton survives Next.js HMR module re-evaluation
// and React Strict Mode double-invocation. Without this, the module resets _client
// to null on each hot reload, creating a second instance that races the first for
// the same Web Lock → "Lock broken by another request with the 'steal' option".
const GLOBAL_KEY = '__supabase_browser_client__';

declare global {
  // eslint-disable-next-line no-var
  var __supabase_browser_client__: ReturnType<typeof createBrowserClient> | undefined;
}

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
    cache = document.cookie.includes(k);
    document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined' ? [] :
  document.cookie.split(';').filter(Boolean).map((c) => {
    const [name, ...rest] = c.trim().split('=');
    return { name: name.trim(), value: decodeURIComponent(rest.join('=')) };
  }).filter((c) => c.name);

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch { return []; }
};

const setCookie = (name: string, value: string, options?: Record<string, unknown>) => {
  let s = `${name}=${encodeURIComponent(value)}; Path=${(options?.path as string) || '/'}; SameSite=None; Secure; Partitioned`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires as string).toUTCString()}`;
  document.cookie = s;
};

export function createClient() {
  // Return existing global singleton if already created
  if (globalThis[GLOBAL_KEY]) return globalThis[GLOBAL_KEY]!;

  globalThis[GLOBAL_KEY] = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        // Disable Web Locks to prevent "Lock broken by another request with the
        // 'steal' option" AbortError. The lock is used to serialize token refreshes
        // but causes races during Next.js navigation, HMR, and React Strict Mode.
        // With a singleton client, serialization is not needed.
        lock: async (_name: string, _acquireTimeout: number, fn: () => Promise<unknown>) => fn(),
      },
      cookies: {
        getAll: () => canUseCookies() ? fromCookies() : fromStorage(),
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          if (canUseCookies()) {
            cookiesToSet.forEach(({ name, value, options }) =>
              value ? setCookie(name, value, options as Record<string, unknown>)
                    : (document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`)
            );
          } else {
            cookiesToSet.forEach(({ name, value, options }) => {
              try {
                value ? localStorage.setItem(`${PFX}${name}`, value)
                      : localStorage.removeItem(`${PFX}${name}`);
              } catch {}
              if (value) setCookie(name, value, options as Record<string, unknown>);
            });
          }
        },
      },
    }
  );

  return globalThis[GLOBAL_KEY]!;
}
